import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../utils/api'

const useAuth = create(
  persist(
    (set, get) => ({
      user: null,
      profile: null,
      token: null,
      isLoading: false,
      hydrated: false,

      _setHydrated: () => set({ hydrated: true }),

      /* Used by /auth/callback to auto-login after email confirmation */
      setTokens: async (accessToken, refreshToken) => {
        localStorage.setItem('access_token', accessToken)
        localStorage.setItem('refresh_token', refreshToken)
        set({ token: accessToken })
        await get().fetchProfile()
      },

      login: async (email, password) => {
        set({ isLoading: true })
        try {
          const { data } = await api.post('/auth/login', { email, password })
          localStorage.setItem('access_token', data.access_token)
          localStorage.setItem('refresh_token', data.refresh_token)
          set({ token: data.access_token, user: data.user, isLoading: false })
          await get().fetchProfile()
          return { ok: true }
        } catch (err) {
          set({ isLoading: false })
          return { ok: false, error: err.response?.data?.detail || 'Login failed' }
        }
      },

      register: async (email, password, phone_number, sms_opt_in) => {
        set({ isLoading: true })
        try {
          const payload = { email, password }
          if (phone_number) payload.phone_number = phone_number
          if (sms_opt_in) payload.sms_opt_in = true
          const { data } = await api.post('/auth/register', payload)
          set({ isLoading: false })
          return { ok: true, data }
        } catch (err) {
          set({ isLoading: false })
          return { ok: false, error: err.response?.data?.detail || 'Registration failed' }
        }
      },

      logout: async () => {
        try { await api.post('/auth/logout') } catch {}
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        set({ user: null, profile: null, token: null })
      },

      fetchProfile: async () => {
        try {
          const { data } = await api.get('/auth/me')
          set({ user: { id: data.id, email: data.email, user_metadata: data.user_metadata || {} }, profile: data.profile })
          return data
        } catch { return null }
      },

      sendPhoneCode: async (phone_number, channel = 'call') => {
        try {
          const { data } = await api.post('/auth/phone/send-code', { phone_number, channel })
          return { ok: true, channel: data.channel }
        } catch (err) {
          return { ok: false, error: err.response?.data?.detail || 'Failed to send code' }
        }
      },

      verifyPhone: async (phone_number, code, channel = 'call') => {
        try {
          await api.post('/auth/phone/verify', { phone_number, code, channel })
          await get().fetchProfile()
          return { ok: true }
        } catch (err) {
          return { ok: false, error: err.response?.data?.detail || 'Invalid code' }
        }
      },

      updateProfile: async (updates) => {
        try {
          const { data } = await api.patch('/auth/me', null, { params: updates })
          // If display_name was updated, refresh user_metadata
          if (updates.display_name !== undefined) {
            const user = get().user
            set({ user: { ...user, user_metadata: { ...user?.user_metadata, display_name: updates.display_name } } })
          }
          // Only update profile if we got profile data back (not just display_name response)
          if (data && !data.display_name) set({ profile: data })
          return { ok: true }
        } catch (err) {
          return { ok: false, error: err.response?.data?.detail || 'Failed to update profile' }
        }
      },

      openBillingPortal: async () => {
        try {
          const { data } = await api.get('/billing/portal')
          window.location.href = data.portal_url
          return { ok: true }
        } catch (err) {
          return { ok: false, error: err.response?.data?.detail || 'Failed to open billing portal' }
        }
      },
    }),
    {
      name: 'crypto-auth',
      partialize: (s) => ({ token: s.token, user: s.user, profile: s.profile }),
      onRehydrateStorage: () => (state) => {
        state?._setHydrated()
        // Re-fetch profile on app load if we have a stored token
        if (state?.token) {
          state.fetchProfile()
        }
      },
    }
  )
)

export default useAuth
